import { PAGEPATHS } from "components/Common/Constant"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import {
  BarChart3,
  TrendingUp,
  CheckCircle,
  XCircle,
  Download,
  Activity,
  Eye,
  Users
} from 'lucide-react'
import "./analytics.css"

const Analytics = (props) => {
    document.title = "Analytics";
    const [scanId, setScanId] = useState(sessionStorage.getItem('scanId'))
    const [phttData, setPshttData] = useState({})
    const [sslyzeData, setSslyzeData] = useState({})
    const [participatesInAnalytics, setParticipatesInAnalytics] = useState(false)
    const [govAnalytics, setGovAnalytics] = useState(false)
    const [dapData, setDapData] = useState({
        dapDetected: '',
        dapParameters: ''
    })

    const breadcrumbItems = [
        { title: "Scan List", link: PAGEPATHS.Scanlist },
        { title: sessionStorage.getItem('domain'), link: "#" },
        { title: "Analytics", link: "#" }
    ]

    useEffect(() => {
        props.setBreadcrumbItems('Analytics', breadcrumbItems)
    })

    const fetchAnalyticsScans = async () => {
        try {
            const { data } = await axiosInstance.get(`${ApiNames.FETCH_ANALYTICS_SCAN}/${scanId}`)
            if (data.status)
                setParticipatesInAnalytics(data.data['Participates in Analytics'])
        } catch (e) {
            console.error(e);
        }
    }

    const fetchGovAnalyticsScans = async () => {
        try {
            const { data } = await axiosInstance.get(`${ApiNames.FETCH_GOV_ANALYTICS_SCAN}/${scanId}`)
            if (data.status)
                setGovAnalytics(data.data)
        } catch (e) {
            console.error(e);
        }
    }

    const fetchDapData = async () => {
        try {
            const { data } = await axiosInstance.get(`${ApiNames.FETCH_DAP_SCAN}/${scanId}`)
            if (data.status && data.data)
                setDapData({
                    dapDetected: data.data.dap_detected || '',
                    dapParameters: data.data.dap_parameters || ''
                })
        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        if (scanId) {
            fetchAnalyticsScans()
            fetchGovAnalyticsScans()
            fetchDapData()
        }
    }, [scanId])

    return (
        <React.Fragment>
            <div className="analytics-page">
                {/* Page Header */}
                <div className="analytics-header">
                    <div className="analytics-header-content">
                        <div className="analytics-icon-wrapper">
                            <BarChart3 className="analytics-icon" />
                        </div>
                        <div className="analytics-header-text">
                            <h1>Analytics & Digital Engagement</h1>
                            <p>Track website traffic, user behavior, and digital analytics program compliance for {sessionStorage.getItem('domain')}</p>
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="analytics-info-box">
                    <div className="analytics-info-header">
                        <div className="analytics-info-icon-wrapper">
                            <Activity className="analytics-info-icon" />
                        </div>
                        <h3>About This Assessment</h3>
                    </div>
                    <div className="analytics-info-content">
                        <p>
                            This assessment evaluates your website's participation in analytics tracking and the Digital Analytics Program (DAP),
                            a government-wide analytics initiative that provides insights into visitor behavior.
                        </p>
                        <p>
                            DAP helps agencies understand how the public finds, accesses, and uses government information and services online.
                        </p>
                    </div>
                </div>

                {/* Status Cards */}
                <div className="analytics-status-grid">
                    {/* Participates in Analytics */}
                    <div className={`analytics-status-card ${participatesInAnalytics ? 'success' : 'error'}`}>
                        <div className="analytics-status-icon-wrapper">
                            {participatesInAnalytics ? (
                                <CheckCircle className="analytics-status-icon success" />
                            ) : (
                                <XCircle className="analytics-status-icon error" />
                            )}
                        </div>
                        <div className="analytics-status-content">
                            <h3>Analytics Participation</h3>
                            <div className={`analytics-status-value ${participatesInAnalytics ? 'success' : 'error'}`}>
                                {participatesInAnalytics.toString()}
                            </div>
                            <p className="analytics-status-label">
                                {participatesInAnalytics ? 'Site participates in analytics tracking' : 'Site does not participate in analytics'}
                            </p>
                        </div>
                    </div>

                    {/* DAP Detected */}
                    <div className={`analytics-status-card ${dapData.dapDetected ? 'success' : 'error'}`}>
                        <div className="analytics-status-icon-wrapper">
                            {dapData.dapDetected ? (
                                <CheckCircle className="analytics-status-icon success" />
                            ) : (
                                <XCircle className="analytics-status-icon error" />
                            )}
                        </div>
                        <div className="analytics-status-content">
                            <h3>DAP Detection</h3>
                            <div className={`analytics-status-value ${dapData.dapDetected ? 'success' : 'error'}`}>
                                {dapData.dapDetected ? dapData.dapDetected.toString() : 'false'}
                            </div>
                            <p className="analytics-status-label">
                                {dapData.dapDetected ? 'Digital Analytics Program detected' : 'DAP not detected on site'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* DAP Parameters */}
                {dapData.dapParameters && (
                    <div className="analytics-data-section">
                        <div className="analytics-section-header">
                            <TrendingUp className="section-header-icon" />
                            <h2>DAP Parameters Detected</h2>
                            <span className="scan-id">Scan ID: {scanId}</span>
                        </div>
                        <div className="analytics-data-content">
                            <div className="analytics-parameters">
                                {dapData.dapParameters}
                            </div>
                        </div>
                    </div>
                )}

                {/* Government Analytics Report */}
                <div className="analytics-data-section">
                    <div className="analytics-section-header">
                        <BarChart3 className="section-header-icon" />
                        <h2>Government Analytics Report</h2>
                        <span className="scan-id">Past 30 Days</span>
                    </div>
                    <div className="analytics-data-content">
                        {/* Metrics Cards */}
                        <div className="analytics-metrics-grid">
                            <div className="analytics-metric-card">
                                <div className="analytics-metric-icon-wrapper blue">
                                    <Users className="analytics-metric-icon" />
                                </div>
                                <div className="analytics-metric-content">
                                    <h4>Domain Visits</h4>
                                    <div className="analytics-metric-value">
                                        {govAnalytics ? govAnalytics['Domain Visits (Past 30 days)'] || 'N/A' : 'N/A'}
                                    </div>
                                    <p className="analytics-metric-label">Unique visitors in last 30 days</p>
                                </div>
                            </div>

                            <div className="analytics-metric-card">
                                <div className="analytics-metric-icon-wrapper purple">
                                    <Eye className="analytics-metric-icon" />
                                </div>
                                <div className="analytics-metric-content">
                                    <h4>Page Views</h4>
                                    <div className="analytics-metric-value">
                                        {govAnalytics ? govAnalytics['Page Views (Past 30 days)'] || 'N/A' : 'N/A'}
                                    </div>
                                    <p className="analytics-metric-label">Total page views in last 30 days</p>
                                </div>
                            </div>
                        </div>

                        {/* Top Downloads */}
                        {govAnalytics && govAnalytics['Top Downloads'] && govAnalytics['Top Downloads'].length > 0 && (
                            <div className="analytics-downloads-section">
                                <div className="analytics-downloads-header">
                                    <Download className="analytics-downloads-icon" />
                                    <h3>Top Downloads</h3>
                                </div>
                                <div className="analytics-downloads-list">
                                    {govAnalytics['Top Downloads'].map((item, index) => (
                                        <div key={index} className="analytics-download-item">
                                            <div className="analytics-download-rank">{index + 1}</div>
                                            <div className="analytics-download-info">
                                                <p className="analytics-download-file">{item.file}</p>
                                            </div>
                                            <div className="analytics-download-hits">
                                                <span className="analytics-download-hits-value">{item.hits}</span>
                                                <span className="analytics-download-hits-label">downloads</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(!govAnalytics || !govAnalytics['Top Downloads'] || govAnalytics['Top Downloads'].length === 0) && (
                            <div className="analytics-empty-state">
                                <Download className="analytics-empty-icon" />
                                <p>No download data available for the past 30 days</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
export default connect(null, { setBreadcrumbItems })(Analytics)